import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  redirect,
  Route,
  RouterProvider,
} from "react-router-dom";
import StockImagesUploader from "pages/StockImagesUploader";
import AssetRetrievals from "pages/AssetRetrievalPage";
import InventoriesPage from "pages/InventoriesPage";
import EnrolledInventoriesPage from "pages/EnrolledInventoriesPage";
import InventoryTemplatesPage from "pages/InventoryTemplatesPage";
import TechnicianInventoryPage from "pages/NewTechnicianInventoryPage";
import ScanPage from "pages/ScanPage";
import OutboundPage from "pages/OutboundPage";
import { getTomorrowAt10AMISO, OrdersPage } from "pages/OrdersPage";
import { BatchesPage } from "pages/BatchesPage";
import { PicklistNewPage } from "pages/Picklist/PicklistNewPage";
import DriversPage from "pages/DriversPage";
import ScanAndDiscover from "pages/ScanAndDiscover";
import VirtualPickup from "pages/VirtualPickup";
import InventoryUploadsPage from "pages/InventoryUploadsPage";
import InventoryPricerUploadsPage from "pages/InventoryPricerUploadsPage";
import ListingsPage from "pages/ListingsPage";
import { ScanInventoryPage } from "pages/PhotoBooth/ScanInventoryPage";
import { ConfigurationPage } from "pages/PhotoBooth/ConfigurationPage";
import { preload } from "swr";
import { fetcher } from "services/api.service";
import { DateTime } from "luxon";
import LogisticsPage from "pages/LogisticsPage";
import FinancePage from "pages/FinancePage";
import BenefitingOrganizationsPage from "pages/BenefitingOrganizationsPage";
import DonationRequestsPage from "pages/DonationRequestsPage";
import SupportPage from "pages/SupportPage";
import React from "react";
import { userHasPermission } from "utils/userPermissions";
import useCurrentUser, { CurrentUser } from "hooks/useCurrentUser";
import { CapturePhotosPage } from "pages/PhotoBooth/CapturePhotosPage";
import { ReviewPhotosPage } from "pages/PhotoBooth/ReviewPhotosPage";
import { PhotoBoothLayout } from "pages/Layouts/PhotoBoothLayout";
import { LoggedInLayout } from "pages/Layouts/LoggedInLayout";
import { GlobalErrorPage } from "pages/Layouts/GlobalErrorPage";
import { AssetRetrievalEditPage } from "pages/AssetRetrievalEditPage";
import PriceComparisonPage from "pages/PriceComparisonPage";
import { StartBatchPage } from "pages/Picklist/Batch/StartBatchPage";
import { PickItemPage } from "pages/Picklist/Batch/PickItemPage";
import { PackItemPage } from "pages/Picklist/Batch/PackItemPage";
import { PickingCompletePage } from "pages/Picklist/Batch/PickingCompletePage";
import { SelectInventoryFromCartPage } from "pages/Picklist/Batch/SelectInventoryFromCartPage";
import { BatchCompletePage } from "pages/Picklist/Batch/BatchCompletePage";
import { ItemCancelledPage } from "pages/Picklist/Batch/ItemCancelledPage";
import { PicklistBatchItemLayout } from "pages/Picklist/Batch/Layouts/PicklistBatchItemLayout";
import { PicklistBatchLayout } from "pages/Picklist/Batch/Layouts/PicklistBatchLayout";
import InventoryGroupRulesPage from "pages/InventoryGroupRulesPage";

import { loader as picklistIndexRouter } from "routes/picklist/root";
import { loader as picklistNewLoader } from "routes/picklist/new";
import { loader as picklistBatchStartLoader } from "routes/picklist/batches/start";
import { loader as picklistBatchRootLoader } from "routes/picklist/batches/root";
import { loader as picklistBatchLayoutLoader } from "routes/picklist/batches/layout";
import { loader as picklistBatchPackingSelectItemLoader } from "routes/picklist/batches/packingSelectItem";
import { loader as picklistBatchPickingCompleteLoader } from "routes/picklist/batches/pickingComplete";
import { loader as picklistBatchItemLayoutLoader } from "routes/picklist/batches/items/layout";
import { loader as picklistBatchItemPickLoader } from "routes/picklist/batches/items/pick";
import { loader as picklistBatchItemPackLoader } from "routes/picklist/batches/items/pack";
import { action as productCatalogEditAction, loader as productCatalogEditLoader } from "routes/productCatalog/edit";
import { action as locationTypeEditAction, loader as locationTypeEditLoader } from "routes/locationTypes/edit";
import { action as locationTypeNewAction, loader as locationTypeNewLoader } from "routes/locationTypes/new";
import { action as locationKindEditAction, loader as locationKindEditLoader } from "routes/locationKinds/edit";
import { action as locationKindNewAction, loader as locationKindNewLoader } from "routes/locationKinds/new";
import { loader as pickupLoader } from "routes/pickups/pickups";
import { loader as addPhotosLoader } from "routes/inboundAddPhotos/inventoryId";
import { action as captureAction, loader as captureLoader } from "routes/inboundAddPhotos/capture";
import { action as reviewAction } from "routes/inboundAddPhotos/review";
import WholesaleIndividualProductCatalogModal from "components/ProductCatalogs/WholesaleIndividualProductCatalogModal";
import { EditPage } from "pages/ProductCatalog/EditPage";
import { ProductCatalogIndexPage } from "pages/ProductCatalogIndexPage";
import { WholesalePage } from "pages/ProductCatalog/WholesalePage";
import { NCPOrdersPage } from "pages/NCPOrdersPage";
import LocationTypeForm from "pages/LocationTypes/LocationTypeForm";
import LocationTypeList from "pages/LocationTypes/LocationTypeList";
import LocationKindForm from "pages/LocationKinds/LocationKindForm";
import LocationKindList from "pages/LocationKinds/LocationKindList";
import LocationForm from "pages/Locations/LocationForm";
import LocationList from "pages/Locations/LocationList";
import { action as locationEditAction, loader as locationEditLoader } from "routes/locations/edit";
import { action as locationNewAction, loader as locationNewLoader } from "routes/locations/new";
import { loader as globalLoader } from "routes/global";
import LocationTypeMovementBlockList from "pages/LocationTypeMovementBlocks/LocationTypeMovementBlockList";
import LocationTypeMovementBlockForm from "pages/LocationTypeMovementBlocks/LocationTypeMovementBlockForm";
import {
  action as locationTypeMovementBlockEditAction,
  loader as locationTypeMovementBlockEditLoader,
} from "routes/locationTypeMovementBlocks/edit";
import {
  action as locationTypeMovementBlockNewAction,
  loader as locationTypeMovementBlockNewLoader,
} from "routes/locationTypeMovementBlocks/new";
import { action as locationTypeMovementBlockDeleteAction } from "routes/locationTypeMovementBlocks/delete";
import LocationTypeMovementConditionList from "pages/LocationTypeMovementConditions/LocationTypeMovementConditionList";
import LocationTypeMovementConditionForm from "pages/LocationTypeMovementConditions/LocationTypeMovementConditionForm";
import {
  action as locationTypeMovementConditionEditAction,
  loader as locationTypeMovementConditionEditLoader,
} from "routes/locationTypeMovementConditions/edit";
import {
  action as locationTypeMovementConditionNewAction,
  loader as locationTypeMovementConditionNewLoader,
} from "routes/locationTypeMovementConditions/new";
import { loader as productCatalogSearchLoader } from "routes/productCatalog/search";
import { action as locationTypeMovementConditionDeleteAction } from "routes/locationTypeMovementConditions/delete";
import MovementActionList from "pages/MovementActions/MovementActionList";
import MovementActionForm from "pages/MovementActions/MovementActionForm";
import { action as movementActionNewAction, loader as movementActionNewLoader } from "routes/movementActions/new";
import { action as movementActionEditAction, loader as movementActionEditLoader } from "routes/movementActions/edit";
import { action as movementActionDeleteAction } from "routes/movementActions/delete";
import AllowedMovementList from "pages/AllowedMovements/AllowedMovementList";
import AllowedMovementForm from "pages/AllowedMovements/AllowedMovementForm";
import { action as allowedMovementNewAction, loader as allowedMovementNewLoader } from "routes/allowedMovements/new";
import { action as allowedMovementEditAction, loader as allowedMovementEditLoader } from "routes/allowedMovements/edit";
import { action as allowedMovementDeleteAction } from "routes/allowedMovements/delete";
import KindMovementRuleList from "pages/KindMovements/KindMovementRuleList";
import KindMovementRuleForm from "pages/KindMovements/KindMovementRuleForm";
import { action as kindMovementNewAction, loader as kindMovementNewLoader } from "routes/kindMovements/new";
import { action as kindMovementEditAction, loader as kindMovementEditLoader } from "routes/kindMovements/edit";
import { action as kindMovementDeleteAction } from "routes/kindMovements/delete";
import { action as forceCompleteAssetRetrievalAction } from "routes/assetRetrievals/forceComplete";
import ScanCard from "components/Receiving/ScanCard";
import VirtualPickupInventoriesNew from "components/Inventories/VirtualPickupInventoriesNew";
import InventoriesNewPage from "pages/InventoriesNewPage";
import InventoryEditPage from "pages/InventoryEditPage";
import { NewPickupsPage } from "pages/NewPickupsPage";
import { ElasticPickupInventoriesPage } from "pages/Pickups/ElasticPickupInventoriesPage";
import CaptureStep from "pages/InboundPhotos/CaptureStep";
import ReviewStep from "pages/InboundPhotos/ReviewStep";
import ScanStep from "pages/InboundPhotos/ScanStep";
import InboundPhotosLayout from "pages/InboundPhotosPage";
import InventoryAddImagesPage from "pages/InventoryAddImagesPage";
import { ReplacementFinderPage, ReplacementFinderPanel } from "pages/ReplacementFinderPage";
import { ReplacementFinderPageError } from "pages/ReplacementFinderPageError";
import {
  action as replacementFinderAction,
  loader as replacementFinderLoader,
} from "routes/replacementFinder/replacementFinder";
import BoomerangPage from "pages/BoomerangPage";
import LoginSso from "./pages/LoginSso";
import { NotFoundPage } from "pages/NotFoundPage";
import { redirectToPickups } from "routes/utils/redirectToPickups";
import { redirectToLogin } from "routes/utils/redirectToLogin";
import { redirectIfUser } from "routes/utils/redirectIfUser";
import { ForceCompleteAssetRetrievalDialog } from "components/AssetRetrievals/ForceCompleteAssetRetrievalDialog";
import { loader as ebayCategoryMetadataLoader } from "routes/ebayCategoryMetadata/show";
import PickupContainersPage from "pages/Pickups/ContainersPage";
import { ElasticProductCatalogPage } from "pages/ElasticProductCatalogPage";
import InventoryPartsIndexPage from "pages/InventoryParts/IndexPage";
import { BulkOrdersPage } from "pages/BulkOrdersPage";
import { action as createBulkOrderAction } from "routes/bulkOrders/new";
import { BulkOrderDialog } from "components/BulkOrder/BulkOrderDialog";
import { action as updateBulkOrderAction } from "routes/bulkOrders/update";
import {
  RegenerateLabelsAssetRetrievalDialog,
  action as regenerateLabelsAssetRetrievalAction,
} from "components/AssetRetrievals/RegenerateLabelsAssetRetrievalDialog";
import ResetTestInventoriesPage from "pages/ResetTestInventoriesPage";
import LogisticsPlatformPage from "pages/LogisticsPlatformPage";

const PermissionedRoute = ({ children, resource }: { children?: React.ReactNode; resource: string }) => {
  const { currentUserPermissions } = useCurrentUser();
  if (userHasPermission(resource, currentUserPermissions)) {
    return <>{children}</>;
  } else {
    return <> You do not have permission to view this page</>;
  }
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="/login" element={<LoginSso />} loader={redirectIfUser} />
      <Route path="/phoenix_login" loader={redirectToLogin} />
      <Route element={<LoggedInLayout />} errorElement={<GlobalErrorPage />} loader={globalLoader} id="global">
        <Route index loader={redirectToPickups} />
        <Route path="/stock-photos" element={<StockImagesUploader />} />
        <Route path="/pickups">
          <Route path=":pickupId" id="pickup" loader={pickupLoader}>
            <Route index element={<ElasticPickupInventoriesPage />} />
            <Route path="containers" element={<PickupContainersPage />} />
            <Route path="inventories/new-virtual-pickup" element={<VirtualPickupInventoriesNew />} />
            <Route path="inventories/new/*" element={<InventoriesNewPage />} />
            <Route path="inventories/:inventoryId/*" element={<InventoryEditPage />} />
            <Route path="add-photos">
              <Route element={<InboundPhotosLayout />}>
                <Route path="scan" element={<ScanStep />} />
                <Route path=":inventoryId" id="receiving-inventory" loader={addPhotosLoader}>
                  <Route path="capture" element={<CaptureStep />} loader={captureLoader} action={captureAction} />
                  <Route path="review" element={<ReviewStep />} action={reviewAction} />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route index element={<NewPickupsPage />} />
        </Route>
        <Route path="/boomerang/*" element={<BoomerangPage />} />
        <Route path="/asset_retrievals/:assetRetrievalUuid" element={<AssetRetrievalEditPage />} />
        <Route path="/asset_retrievals/*" element={<AssetRetrievals />}>
          <Route
            path=":retrievalId/force_complete"
            action={forceCompleteAssetRetrievalAction}
            element={<ForceCompleteAssetRetrievalDialog />}
          />
          <Route
            path=":retrievalId/regenerate-labels"
            action={regenerateLabelsAssetRetrievalAction}
            element={<RegenerateLabelsAssetRetrievalDialog />}
          />
        </Route>
        <Route path="/support">
          <Route path="asset_retrievals">
            <Route index element={<AssetRetrievals />} />
            <Route path=":assetRetrievalUuid" element={<AssetRetrievalEditPage />} />
          </Route>
        </Route>
        <Route path="/inventories/*" element={<InventoriesPage />} />
        <Route path="/enrolled" element={<EnrolledInventoriesPage />} />
        <Route path="/inventory-templates" element={<InventoryTemplatesPage sx={{ mx: 10, my: 8 }} />} />
        <Route path="/inventory-templates/:templateUuid" element={<InventoryTemplatesPage sx={{ mx: 10, my: 8 }} />} />
        <Route path="/inventories/:inventoryId" element={<TechnicianInventoryPage />} />
        <Route path="/inventory-parts" element={<InventoryPartsIndexPage />} />
        <Route path="/scan" element={<ScanPage />} />
        <Route path="/technician" element={<TechnicianInventoryPage />}>
          <Route path=":inventoryId">
            <Route path="inventory-parts/:type" />
          </Route>
        </Route>
        <Route path="/receiving/*">
          <Route index element={<ScanCard />} />
          <Route path=":inventoryId" element={<InventoryAddImagesPage />} />
        </Route>
        <Route path="/outbound" element={<OutboundPage />} />
        <Route path="/ncp-orders" element={<NCPOrdersPage />}>
          <Route
            path="due-today"
            loader={async () => {
              const { warehouseName, warehouseTimezone } = await fetcher<CurrentUser>("/current_user");
              const tomorrowAt10AMISO = getTomorrowAt10AMISO(warehouseTimezone);
              return redirect(
                `/ncp-orders?sort=shipBy&filter[]=ship_by,on_or_before,${tomorrowAt10AMISO}&filter[]=warehouse,is,${warehouseName}&filter[]=state,not,packed`,
              );
            }}
          />
        </Route>
        <Route path="/orders" element={<OrdersPage />}>
          <Route
            path="due-today"
            loader={async () => {
              const { warehouseName, warehouseTimezone } = await fetcher<CurrentUser>("/current_user");
              const tomorrowAt10AMISO = getTomorrowAt10AMISO(warehouseTimezone);
              const recent = `ordered_at,after,${DateTime.now().minus({ days: 10 }).toISODate()}`;
              return redirect(
                `/orders?sort=shipBy&filter[]=${recent}&filter[]=ship_by,on_or_before,${tomorrowAt10AMISO}&filter[]=warehouse,is,${warehouseName}&filter[]=state,not,packed`,
              );
            }}
          />
        </Route>
        <Route path="/bulk-orders" element={<BulkOrdersPage />}>
          <Route
            path="wholesale"
            loader={async () => {
              return redirect(`/bulk-orders?filter[]=type,contains,WholesaleOrder&filter[]=state,not,completed`);
            }}
          />
          <Route
            path="donations"
            loader={async () => {
              return redirect(`/bulk-orders?filter[]=type,contains,DonationOrder&filter[]=state,not,completed`);
            }}
          />
          <Route path="new" action={createBulkOrderAction} element={<BulkOrderDialog />} />
          <Route
            path=":manifestId/update"
            action={updateBulkOrderAction}
            element={<Navigate to="/bulk-orders" replace />}
          />
        </Route>
        <Route path="/batches" element={<BatchesPage />} />
        <Route path="/picklist">
          <Route index loader={picklistIndexRouter} />
          <Route path="new" element={<PicklistNewPage />} loader={picklistNewLoader} />
          <Route path="batches/:batchId/start" element={<StartBatchPage />} loader={picklistBatchStartLoader} />
          <Route path="batches/:batchId" loader={picklistBatchRootLoader} />
          <Route
            path="batches/:batchId"
            element={<PicklistBatchLayout />}
            loader={picklistBatchLayoutLoader}
            id={"picklist-batch"}
          >
            <Route path="complete" element={<BatchCompletePage />} />
            <Route
              path="packing-select-item"
              element={<SelectInventoryFromCartPage />}
              loader={picklistBatchPackingSelectItemLoader}
            />
            <Route
              path="picking-complete"
              element={<PickingCompletePage />}
              loader={picklistBatchPickingCompleteLoader}
            />
            <Route
              path="items/:itemId"
              element={<PicklistBatchItemLayout />}
              loader={picklistBatchItemLayoutLoader}
              id="picklist-batch-item"
            >
              <Route path="cancelled" element={<ItemCancelledPage />} />
              <Route path="pick" element={<PickItemPage />} loader={picklistBatchItemPickLoader} />
              <Route path="pack" element={<PackItemPage />} loader={picklistBatchItemPackLoader} />
            </Route>
          </Route>
        </Route>
        <Route path="/drivers" element={<DriversPage />}></Route>
        <Route path="/scan-and-discover" element={<ScanAndDiscover />}></Route>
        <Route path="/virtual-pickup" element={<VirtualPickup />}></Route>
        <Route path="/inventory-uploads" element={<InventoryUploadsPage />}></Route>
        <Route path="/inventory-pricer-uploads" element={<InventoryPricerUploadsPage />}></Route>
        <Route path="/listings/*" element={<ListingsPage />} />
        <Route path="/location-types/*">
          <Route index element={<LocationTypeList />} />
          <Route
            path="new"
            element={<LocationTypeForm />}
            id="location-type-new"
            loader={locationTypeNewLoader}
            action={locationTypeNewAction}
          />
          <Route
            path=":locationTypeId/edit"
            element={<LocationTypeForm />}
            id="location-type-edit"
            loader={locationTypeEditLoader}
            action={locationTypeEditAction}
          />
        </Route>
        <Route path="/location-kinds/*">
          <Route index element={<LocationKindList />} />
          <Route
            path="new"
            element={<LocationKindForm />}
            id="location-kind-new"
            loader={locationKindNewLoader}
            action={locationKindNewAction}
          />
          <Route
            path=":locationKindId/edit"
            element={<LocationKindForm />}
            id="location-kind-edit"
            loader={locationKindEditLoader}
            action={locationKindEditAction}
          />
        </Route>
        <Route path="/locations/*">
          <Route index element={<LocationList />} />
          <Route path="new" element={<LocationForm />} loader={locationNewLoader} action={locationNewAction} />
          <Route
            path=":locationId/edit"
            element={<LocationForm />}
            loader={locationEditLoader}
            action={locationEditAction}
          />
        </Route>
        <Route path="/product-catalog">
          <Route index element={<ProductCatalogIndexPage />} />
          <Route path="elastic" element={<ElasticProductCatalogPage />} />

          {/* This route exists for the short term until product catalog index uses a loader */}
          <Route path="search" loader={productCatalogSearchLoader} />
          <Route path="wholesale" element={<WholesalePage />}>
            <Route path="confirm" element={<WholesaleIndividualProductCatalogModal />} />
            <Route path="confirmAll" element={<WholesaleIndividualProductCatalogModal filterMode={true} />} />
          </Route>
          <Route
            path=":productId"
            element={<EditPage />}
            loader={productCatalogEditLoader}
            action={productCatalogEditAction}
          />
        </Route>
        <Route path="/resale-photo-booth" element={<PhotoBoothLayout />}>
          <Route index element={<ScanInventoryPage />} />
          <Route path="configuration" element={<ConfigurationPage />} />
          <Route
            path=":inventoryId"
            errorElement={<ScanInventoryPage />}
            loader={async ({ params }) => {
              return preload(`/inventories/${params.inventoryId}`, fetcher);
            }}
          >
            <Route index element={<Navigate replace={true} to="capture/1" />} />
            <Route path="capture/:photoSet" element={<CapturePhotosPage />} />
            <Route path="review" element={<ReviewPhotosPage />} />
          </Route>
        </Route>

        <Route
          path="/logistics/*"
          element={
            <PermissionedRoute resource={"logistics"}>
              <LogisticsPage />
            </PermissionedRoute>
          }
        />
        <Route
          path="/logistics_platform/*"
          element={
            <PermissionedRoute resource={"logistics"}>
              <LogisticsPlatformPage />
            </PermissionedRoute>
          }
        />
        <Route
          path="/finance/*"
          element={
            <PermissionedRoute resource={"finance"}>
              <FinancePage />
            </PermissionedRoute>
          }
        />
        <Route
          path="/benefiting-organization/*"
          element={
            <PermissionedRoute resource={"tech_access"}>
              <BenefitingOrganizationsPage />
            </PermissionedRoute>
          }
        ></Route>
        <Route
          path="/donation-requests/*"
          element={
            <PermissionedRoute resource={"tech_access"}>
              <DonationRequestsPage />
            </PermissionedRoute>
          }
        ></Route>
        <Route path="/replacement-finder" element={<ReplacementFinderPage />} action={replacementFinderAction}>
          <Route
            path=":inventoryId"
            element={<ReplacementFinderPanel />}
            errorElement={<ReplacementFinderPageError />}
            loader={replacementFinderLoader}
          />
        </Route>
        <Route
          path="/support/*"
          element={
            <PermissionedRoute resource={"support"}>
              <SupportPage />
            </PermissionedRoute>
          }
        ></Route>
        <Route
          path="/price-comparison"
          element={
            <PermissionedRoute resource={"admin"}>
              <PriceComparisonPage />
            </PermissionedRoute>
          }
        />
        <Route
          path="/reset-test-inventories"
          element={
            <PermissionedRoute resource={"admin"}>
              <ResetTestInventoriesPage />
            </PermissionedRoute>
          }
        />
        <Route
          path="/inventory-group-rules"
          element={
            <PermissionedRoute resource={"admin"}>
              <InventoryGroupRulesPage />
            </PermissionedRoute>
          }
        />
        <Route path="/location-type-movement-blocks/*">
          <Route index element={<LocationTypeMovementBlockList />} />
          <Route
            path="new"
            element={<LocationTypeMovementBlockForm />}
            loader={locationTypeMovementBlockNewLoader}
            action={locationTypeMovementBlockNewAction}
          />
          <Route
            path=":locationTypeMovementBlockId/edit"
            element={<LocationTypeMovementBlockForm />}
            loader={locationTypeMovementBlockEditLoader}
            action={locationTypeMovementBlockEditAction}
          />
          <Route path=":locationTypeMovementBlockId/delete" action={locationTypeMovementBlockDeleteAction} />
        </Route>
        <Route path="/location-type-movement-conditions/*">
          <Route index element={<LocationTypeMovementConditionList />} />
          <Route
            path="new"
            element={<LocationTypeMovementConditionForm />}
            loader={locationTypeMovementConditionNewLoader}
            action={locationTypeMovementConditionNewAction}
          />
          <Route
            path=":locationTypeMovementConditionId/edit"
            element={<LocationTypeMovementConditionForm />}
            loader={locationTypeMovementConditionEditLoader}
            action={locationTypeMovementConditionEditAction}
          />
          <Route path=":locationTypeMovementConditionId/delete" action={locationTypeMovementConditionDeleteAction} />
        </Route>
        <Route path="/movement-actions/*">
          <Route index element={<MovementActionList />} />
          <Route
            path="new"
            element={<MovementActionForm />}
            loader={movementActionNewLoader}
            action={movementActionNewAction}
          />
          <Route
            path=":movementActionId/edit"
            element={<MovementActionForm />}
            loader={movementActionEditLoader}
            action={movementActionEditAction}
          />
          <Route path=":movementActionId/delete" action={movementActionDeleteAction} />
        </Route>
        <Route path="/allowed-movements/*">
          <Route index element={<AllowedMovementList />} />
          <Route
            path="new"
            element={<AllowedMovementForm />}
            loader={allowedMovementNewLoader}
            action={allowedMovementNewAction}
          />
          <Route
            path=":allowedMovementId/edit"
            element={<AllowedMovementForm />}
            loader={allowedMovementEditLoader}
            action={allowedMovementEditAction}
          />
          <Route path=":allowedMovementId/delete" action={allowedMovementDeleteAction} />
        </Route>
        <Route path="/kind-movements/*">
          <Route index element={<KindMovementRuleList />} />
          <Route
            path="new"
            element={<KindMovementRuleForm />}
            loader={kindMovementNewLoader}
            action={kindMovementNewAction}
          />
          <Route
            path=":kindMovementRuleId/edit"
            element={<KindMovementRuleForm />}
            loader={kindMovementEditLoader}
            action={kindMovementEditAction}
          />
          <Route path=":kindMovementRuleId/delete" action={kindMovementDeleteAction} />
        </Route>
        <Route path={"ebay-category-metadata/:categoryId"} loader={ebayCategoryMetadataLoader} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="*" loader={redirectToLogin} />
    </Route>,
  ),
);

export function Router() {
  return <RouterProvider router={router} />;
}
